import { createContext, useCallback, useMemo, useState } from 'react'
import Facebook from '~/lib/facebook'

export const FacebookContext = createContext(null)

const FacebookProvider = ({ children, appId }) => {
  if (!appId) {
    throw new Error('Facebook provider needs an app ID')
  }

  const [isInitialized, setIsInitialized] = useState(false)
  const [facebook, setFacebook] = useState(null)

  const initialize = useCallback(() => {
    return new Promise(resolve => {
      if (isInitialized) {
        return resolve(facebook)
      }

      if (window.FB) {
        const fb = new Facebook(window.FB)
        setFacebook(fb)
        setIsInitialized(true)
        return resolve(fb)
      }

      window.fbAsyncInit = function () {
        window.FB.init({
          appId,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v9.0',
        })

        const fb = new Facebook(window.FB)
        setFacebook(fb)
        setIsInitialized(true)
        resolve(fb)
      }

      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.defer = true
      script.crossorigin = 'anonymous'
      script.src = 'https://connect.facebook.net/en_US/sdk.js'
      document.body.appendChild(script)
    })
  }, [appId, facebook, isInitialized])

  const login = useCallback(
    async options => {
      const fb = await initialize()

      return new Promise(async (resolve, reject) => {
        try {
          const authResponse = await fb.login(options)
          const profile = await fb.profile()

          resolve({ ...authResponse, profile })
        } catch (error) {
          reject(error)
        }
      })
    },
    [initialize]
  )

  const loginStatus = useCallback(async () => {
    const fb = await initialize()

    return fb.loginStatus()
  }, [initialize])

  const logout = useCallback(async () => {
    const fb = await initialize()

    return new Promise(async resolve => {
      try {
        await fb.logout()
      } catch {}

      resolve()
    })
  }, [initialize])

  const share = useCallback(
    async href => {
      const fb = await initialize()

      return fb.share(href)
    },
    [initialize]
  )

  const value = useMemo(() => {
    return {
      initialize,
      isInitialized,
      login,
      logout,
      share,
      loginStatus,
    }
  }, [initialize, isInitialized, login, logout, share, loginStatus])

  return <FacebookContext.Provider value={value}>{children}</FacebookContext.Provider>
}

export default FacebookProvider
